import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { clearSession, fetchMe, getAccessToken, getStoredUser, type AuthUser } from "./api";

export type SessionState =
  | { status: "loading" }
  | { status: "guest" }
  | { status: "authenticated"; user: AuthUser };

type SessionContextValue = {
  session: SessionState;
  setUser: (user: AuthUser | null) => void;
  refresh: () => Promise<void>;
};

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionState>(() => {
    const cached = getStoredUser();
    // If we have a cached user AND a token, render as authenticated immediately.
    // Otherwise: no token → guest right away (no /auth/me call → no 401 log).
    if (cached && getAccessToken()) return { status: "authenticated", user: cached };
    if (getAccessToken()) return { status: "loading" };
    return { status: "guest" };
  });

  const refresh = useCallback(async () => {
    // Only hit /auth/me if we actually have a token. Avoids the spurious 401 on
    // every public-page load.
    if (!getAccessToken()) {
      setSession({ status: "guest" });
      return;
    }
    try {
      const user = await fetchMe();
      setSession({ status: "authenticated", user });
    } catch {
      clearSession();
      setSession({ status: "guest" });
    }
  }, []);

  useEffect(() => {
    // Background validation: even when we render optimistically from cache,
    // re-fetch /auth/me to detect server-side revocation. No call if no token.
    if (getAccessToken()) void refresh();
  }, [refresh]);

  const setUser = useCallback((user: AuthUser | null) => {
    if (user) setSession({ status: "authenticated", user });
    else {
      clearSession();
      setSession({ status: "guest" });
    }
  }, []);

  const value = useMemo<SessionContextValue>(() => ({ session, setUser, refresh }), [session, setUser, refresh]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside SessionProvider");
  return ctx;
}
